import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Mail, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_unified-calendar-app-2/artifacts/7wohqirg_Texto%20del%20pa%CC%81rrafo.jpeg";

export default function Login() {
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [loadingMagic, setLoadingMagic] = useState(false);
  const [loadingPin, setLoadingPin] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [pinEmail, setPinEmail] = useState("");
  const [pin, setPin] = useState("");
  const [pinStatus, setPinStatus] = useState({ configured: false });

  const navigate = useNavigate();
  const { user, setToken } = useAuth();

  useEffect(() => {
    if (user) navigate("/app");
  }, [user, navigate]);

  useEffect(() => {
    api.get("/auth/staff/pin/status").then(({ data }) => setPinStatus(data)).catch(() => {});
  }, []);

  const handleGoogleLogin = async () => {
    setLoadingGoogle(true);
    try {
      const { data } = await api.get("/auth/google/login");
      window.location.href = data.authorization_url;
    } catch (e) {
      console.error(e);
      setLoadingGoogle(false);
    }
  };

  const handleMagicRequest = async (e) => {
    e.preventDefault();
    if (!magicEmail) return;
    setLoadingMagic(true);
    try {
      await api.post("/auth/staff/magic/request", { email: magicEmail });
      setMagicSent(true);
      toast.success("Revisa tu correo en unos segundos");
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo enviar el enlace";
      toast.error(msg);
    } finally {
      setLoadingMagic(false);
    }
  };

  const handlePinLogin = async (e) => {
    e.preventDefault();
    if (!pinEmail || pin.length !== 6) {
      toast.error("Ingresa tu correo y un PIN de 6 dígitos");
      return;
    }
    setLoadingPin(true);
    try {
      const { data } = await api.post("/auth/staff/pin/login", { email: pinEmail, pin });
      if (setToken) await setToken(data.token);
      else localStorage.setItem("agenda_token", data.token);
      toast.success("Sesión iniciada");
      window.location.href = "/app";
    } catch (err) {
      const msg = err?.response?.data?.detail || "PIN incorrecto";
      toast.error(msg);
      setPin("");
    } finally {
      setLoadingPin(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F4EFE4]">
      <div className="px-6 py-4">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#6F5A4C] hover:text-[#B5704A] transition-colors" data-testid="back-to-home">
          <ArrowLeft className="w-4 h-4" /> Volver al sitio
        </Link>
      </div>
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="flex items-center justify-center p-8 lg:p-16">
          <div className="w-full max-w-md">
            <div className="flex items-center gap-3 mb-10">
              <img src={LOGO_URL} alt="Amarme" className="w-14 h-14 rounded-full object-cover border border-[#B5704A]/20" />
              <div className="leading-tight">
                <div className="brand-wordmark text-lg text-[#3A2418]">AMARME</div>
                <div className="font-script text-sm text-[#B5704A] -mt-0.5">Skin</div>
              </div>
            </div>
            <span className="label-eyebrow text-[#B5704A]">Panel staff</span>
            <h1 className="font-display text-4xl lg:text-5xl font-[300] text-[#3A2418] mt-2">
              Bienvenida <span className="italic font-[500] text-[#B5704A]">de vuelta</span>
            </h1>
            <p className="text-[#6F5A4C] mt-3 text-sm">
              Elige cómo quieres entrar a tu panel.
            </p>

            <Tabs defaultValue="google" className="mt-8">
              <TabsList className="grid grid-cols-3 w-full bg-white border border-[#E8DFD2] rounded-full h-11 p-1" data-testid="login-tabs">
                <TabsTrigger value="google" className="rounded-full text-xs data-[state=active]:bg-[#3A2418] data-[state=active]:text-white text-[#6F5A4C]" data-testid="tab-google">Google</TabsTrigger>
                <TabsTrigger value="magic" className="rounded-full text-xs data-[state=active]:bg-[#3A2418] data-[state=active]:text-white text-[#6F5A4C]" data-testid="tab-magic">Enlace</TabsTrigger>
                <TabsTrigger value="pin" className="rounded-full text-xs data-[state=active]:bg-[#3A2418] data-[state=active]:text-white text-[#6F5A4C]" data-testid="tab-pin">PIN</TabsTrigger>
              </TabsList>

              {/* Google */}
              <TabsContent value="google" className="mt-6">
                <Button
                  onClick={handleGoogleLogin}
                  disabled={loadingGoogle}
                  className="w-full h-12 bg-white border border-[#E8DFD2] text-[#3A2418] hover:bg-[#FAF7F1] hover:text-[#3A2418] rounded-full justify-start px-6"
                  data-testid="google-login-button"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  {loadingGoogle ? "Redirigiendo…" : "Continuar con Google"}
                </Button>
                <p className="text-xs text-[#6F5A4C] mt-4 leading-relaxed">
                  Sincroniza tu calendario automáticamente con cada cita.
                </p>
              </TabsContent>

              {/* Magic Link */}
              <TabsContent value="magic" className="mt-6">
                {magicSent ? (
                  <div className="bg-white border border-[#E8DFD2] rounded-2xl p-6 text-center" data-testid="magic-sent-box">
                    <Mail className="w-10 h-10 text-[#B5704A] mx-auto mb-3" />
                    <h3 className="font-display text-xl text-[#3A2418] mb-1">Revisa tu correo</h3>
                    <p className="text-sm text-[#6F5A4C] leading-relaxed">
                      Si el correo está autorizado, te enviamos un enlace para entrar. Caduca en 15 minutos.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setMagicSent(false); setMagicEmail(""); }}
                      className="text-xs text-[#B5704A] underline mt-4"
                      data-testid="magic-resend-link"
                    >
                      Enviar a otro correo
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleMagicRequest} className="space-y-4" data-testid="magic-form">
                    <div>
                      <Label className="text-sm text-[#3A2418]">Correo electrónico</Label>
                      <Input
                        type="email"
                        required
                        value={magicEmail}
                        onChange={(e) => setMagicEmail(e.target.value)}
                        placeholder="tu@correo.com"
                        className="mt-1.5 h-11 bg-white border-[#E8DFD2]"
                        data-testid="magic-email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loadingMagic}
                      className="w-full h-12 bg-[#3A2418] text-white hover:bg-[#3A2418]/90 rounded-full"
                      data-testid="magic-submit-button"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      {loadingMagic ? "Enviando enlace…" : "Enviar enlace al correo"}
                    </Button>
                    <p className="text-xs text-[#6F5A4C] leading-relaxed">
                      Sin contraseñas. Te llega un enlace que abres una vez y entras.
                    </p>
                  </form>
                )}
              </TabsContent>

              {/* PIN */}
              <TabsContent value="pin" className="mt-6">
                {!pinStatus.configured ? (
                  <div className="bg-white border border-[#E8DFD2] rounded-2xl p-6 text-center" data-testid="pin-not-configured">
                    <KeyRound className="w-10 h-10 text-[#B5704A] mx-auto mb-3" />
                    <h3 className="font-display text-xl text-[#3A2418] mb-1">Configura tu PIN primero</h3>
                    <p className="text-sm text-[#6F5A4C] leading-relaxed">
                      Para usar PIN, entra primero con el enlace mágico y configúralo en Ajustes.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handlePinLogin} className="space-y-4" data-testid="pin-form">
                    <div>
                      <Label className="text-sm text-[#3A2418]">Correo electrónico</Label>
                      <Input
                        type="email"
                        required
                        value={pinEmail}
                        onChange={(e) => setPinEmail(e.target.value)}
                        placeholder="tu@correo.com"
                        className="mt-1.5 h-11 bg-white border-[#E8DFD2]"
                        data-testid="pin-email-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-[#3A2418]">PIN de 6 dígitos</Label>
                      <Input
                        type="password"
                        inputMode="numeric"
                        pattern="\d{6}"
                        maxLength={6}
                        required
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                        placeholder="••••••"
                        className="mt-1.5 h-11 bg-white border-[#E8DFD2] text-center tracking-[0.5em] text-xl"
                        data-testid="pin-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loadingPin}
                      className="w-full h-12 bg-[#3A2418] text-white hover:bg-[#3A2418]/90 rounded-full"
                      data-testid="pin-submit-button"
                    >
                      <KeyRound className="w-4 h-4 mr-2" />
                      {loadingPin ? "Verificando…" : "Entrar con PIN"}
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>

            <p className="text-xs text-[#6F5A4C] mt-8 leading-relaxed">
              Solo el correo autorizado puede acceder al panel.
            </p>
          </div>
        </div>
        <div className="hidden lg:block relative">
          <img
            src="https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?auto=format&fit=crop&w=900&q=80"
            alt="Tratamiento facial"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#3A2418]/20 via-transparent to-[#3A2418]/60"></div>
          <div className="absolute bottom-12 left-12 right-12 text-white">
            <p className="font-display text-3xl font-[300] leading-snug italic">
              "El cuidado de la piel es <span className="font-script text-4xl text-[#FAE0CE]">amor propio</span> en su forma más pura."
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
